<template>
  <div class="panel-card">
    <div class="panel-header">
      <!-- Drag indicator (grip dots) - appears on widget hover -->
      <div class="drag-indicator" title="Drag to reorder">
        <span class="grip-dots"></span>
      </div>
      <h3 class="section-title">
        <i v-if="icon" :class="icon" class="section-icon"></i>
        {{ title }}
      </h3>
      <div class="header-actions">
        <slot name="header-actions">
          <router-link
            v-if="viewAllLink"
            :to="viewAllLink"
            class="view-all-link"
          >
            View All
          </router-link>
        </slot>
      </div>
    </div>

    <div v-if="showProgress" class="progress-section">
      <div class="progress-info">
        <span>{{ progressLabel }}</span>
        <span>{{ progressValue }}%</span>
      </div>
      <div class="progress">
        <div
          class="progress-bar"
          :style="{ width: progressValue + '%' }"
        ></div>
      </div>
    </div>

    <div class="panel-body">
      <div v-if="loading" class="empty-state">
        <div class="empty-icon">
          <i class="fa fa-spinner fa-spin"></i>
        </div>
        <p class="empty-text">Loading...</p>
      </div>

      <div v-else-if="restricted" class="empty-state empty-state--restricted">
        <div class="empty-icon">
          <i class="fa fa-lock"></i>
        </div>
        <p class="empty-title">Access Restricted</p>
        <p class="empty-text">{{ restrictedMessage }}</p>
      </div>

      <div v-else-if="isEmpty" class="empty-state">
        <div class="empty-icon">
          <i :class="emptyIcon || 'fa fa-inbox'"></i>
        </div>
        <p class="empty-title">{{ emptyTitle || 'No items' }}</p>
        <p class="empty-text">{{ emptyText || 'Nothing to display' }}</p>
      </div>

      <slot v-else></slot>
    </div>

    <div v-if="$slots.footer" class="panel-footer">
      <slot name="footer"></slot>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DashboardWidget',

  props: {
    title: {
      type: String,
      required: true
    },
    icon: {
      type: String,
      default: null
    },
    viewAllLink: {
      type: [String, Object],
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    isEmpty: {
      type: Boolean,
      default: false
    },
    emptyIcon: {
      type: String,
      default: null
    },
    emptyTitle: {
      type: String,
      default: null
    },
    emptyText: {
      type: String,
      default: null
    },
    showProgress: {
      type: Boolean,
      default: false
    },
    progressLabel: {
      type: String,
      default: 'Progress'
    },
    progressValue: {
      type: Number,
      default: 0
    },
    // Restricted-state props (matches the restricted mode on the shared
    // EmptyState component). When `restricted` is true, the body renders
    // an Access Restricted panel instead of the slot/empty/loading
    // states. `subject` is the CASL feature key (e.g. 'overdue_recall')
    // used to look up the responsible-list label for the message.
    restricted: {
      type: Boolean,
      default: false
    },
    subject: {
      type: String,
      default: ''
    }
  },
  computed: {
    restrictedMessage() {
      // Inline lookup to avoid importing src/utils/permissions here just
      // to derive copy — DashboardWidget is a leaf UI component. Keep the
      // label map in sync with src/utils/permissions.js.
      const responsibleLabels = {
        'overdue_recall': 'Overdue Recall responsible employees',
        'unscheduled_treatment': 'Unscheduled Treatment responsible employees',
        'broken_appointment': 'Broken Appointments responsible employees',
        'outstanding_claim': 'Outstanding Claims responsible employees',
        'outstanding_preauths': 'Outstanding Pre-Authorizations responsible employees',
        'patient_balances': 'Patient Balances responsible employees',
      };
      const label = responsibleLabels[this.subject];
      if (label) return `Ask your admin to add you to the ${label} in Settings.`;
      return `Ask your admin to grant your role access to ${this.title}.`;
    }
  }
}
</script>

<style lang="scss" scoped>
// Base styles that respond to CSS custom properties for sizing
// Additional styles are in AdminPageModern.scss for consistency

.panel-card {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--widget-header-padding, 16px 20px);
  border-bottom: 1px solid #e2e8f0;
  background-color: #f8fafc;
  border-radius: 12px 12px 0 0;
  flex-shrink: 0;
  position: relative;

  // Drag indicator - grip dots that appear on widget hover
  .drag-indicator {
    position: absolute;
    left: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 16px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.15s ease;
    cursor: grab;

    &:active {
      cursor: grabbing;
    }

    .grip-dots {
      display: block;
      width: 8px;
      height: 14px;
      background-image: radial-gradient(circle, #94a3b8 1.5px, transparent 1.5px);
      background-size: 4px 4px;
      background-position: 0 0;
    }
  }

  .section-title {
    font-size: 1rem;
    font-weight: 600;
    color: #0f172a;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 10px;
    transition: padding-left 0.15s ease;
  }

  .section-icon {
    color: #64748b;
    font-size: 1rem;
    width: 20px;
    text-align: center;
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .view-all-link {
    font-size: 0.8125rem;
    color: #3abf94;
    text-decoration: none;
    font-weight: 500;
    transition: color 0.2s ease;

    &:hover {
      color: darken(#3abf94, 8%);
    }
  }
}

.panel-body {
  padding: 0;
  flex: 1;
  overflow: hidden;
}

.panel-footer {
  padding: 12px 20px;
  border-top: 1px solid #e2e8f0;
  background-color: #f8fafc;
  text-align: center;
  flex-shrink: 0;
}

.progress-section {
  padding: var(--widget-stat-padding, 12px 20px);
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;

  .progress-info {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
    font-size: 0.75rem;
    color: #64748b;
  }

  .progress {
    height: 6px;
    background-color: #e2e8f0;
    border-radius: 3px;
    overflow: hidden;

    .progress-bar {
      background-color: #3abf94;
      border-radius: 3px;
    }
  }
}

.empty-state {
  padding: 2rem;
  text-align: center;
  color: #64748b;

  .empty-icon {
    font-size: 2rem;
    color: #94a3b8;
    margin-bottom: 0.75rem;

    i, .fa {
      font-size: inherit;
      color: inherit;
    }
  }

  .empty-title {
    font-size: 0.9375rem;
    font-weight: 600;
    color: #475569;
    margin-bottom: 4px;
  }

  .empty-text {
    font-size: 0.8125rem;
    color: #94a3b8;
    margin: 0;
  }
}

// Restricted-mode color treatment. Distinct enough to scan but informational,
// not alarming — this is a permission situation, not an error.
.empty-state--restricted {
  .empty-icon {
    color: #d6890c;
  }

  .empty-title {
    color: #d6890c;
  }
}
</style>

<!-- Unscoped styles for slot content - ensures consistent stats styling regardless of Vue's scope behavior -->
<style lang="scss">
.panel-card .panel-body {
  // Widget stats styling - overrides _base.scss global .stat-item styles
  .stats-row {
    display: flex;
    justify-content: space-evenly;
    padding: var(--widget-stat-padding, 12px 20px);
    border-bottom: 1px solid #e8ecf1;
    margin-bottom: 0;
    min-height: 60px;

    .stat-item {
      display: block !important;
      text-align: center;
      flex: 1;
      min-width: 0;
      padding: 0 12px !important;
      border-left: none !important;
      position: relative;

      // Separator between stat items
      & + .stat-item::before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        height: 24px;
        width: 1px;
        background-color: #e2e8f0;
      }

      .stat-value {
        display: block;
        font-size: var(--widget-stat-size, 1.5rem);
        font-weight: 700;
        color: #334155;
        line-height: 1.2;

        // Color variants for different stat types
        &.cards { color: #3b82f6; }
        &.columns { color: #8b5cf6; }
        &.pinned { color: #f59e0b; }
        &.templates { color: #8b5cf6; }
        &.has-items { color: #f59e0b; }
        &.completed { color: #22c55e; }
        &.overdue { color: #ef4444; }
        &.active { color: #22c55e; }
        &.inactive { color: #94a3b8; }
        &.total { color: #334155; }
      }

      .stat-label {
        font-size: 0.6875rem;
        color: #64748b;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-top: 2px;
      }
    }
  }
}
</style>
